import { Link, useRouteError } from "react-router-dom";

import { ApiError } from "@/shared/api/errors";
import { Alert, Button, Card } from "@/shared/ui";

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <Card title={title} className="w-full max-w-md">
        {children}
      </Card>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <Shell title="Page not found">
      <p className="text-muted text-sm">
        That page does not exist, or you do not have access to it.
      </p>
      <Button className="mt-4" onClick={() => window.history.back()}>
        Go back
      </Button>
    </Shell>
  );
}

export function ForbiddenPage() {
  return (
    <Shell title="No access">
      <p className="text-muted text-sm">
        Your account does not have permission to view this area. If you think that is wrong,
        contact your school administrator.
      </p>
      <Link to="/" className="mt-4 inline-block">
        <Button variant="secondary">Back to dashboard</Button>
      </Link>
    </Shell>
  );
}

/**
 * Route-level error boundary.
 *
 * Always surfaces the request id: it is the single key support needs to find
 * the trace, and asking a school clerk to "check the console" is not a support
 * process. docs/07-frontend.md §11
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const requestId = error instanceof ApiError ? error.requestId : undefined;

  return (
    <Shell title="Something went wrong">
      <Alert tone="danger">
        {error instanceof Error ? error.message : "An unexpected error occurred."}
      </Alert>
      {requestId && (
        <p className="text-muted mt-3 text-xs">
          Reference for support: <code className="font-mono">{requestId}</code>
        </p>
      )}
      <div className="mt-4 flex gap-2">
        <Button onClick={() => window.location.reload()}>Reload</Button>
        <Link to="/">
          <Button variant="secondary">Go home</Button>
        </Link>
      </div>
    </Shell>
  );
}
